import {ProgressAria} from '_components/ProgressAria';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import dayjs from 'dayjs';
import {isEmpty} from 'lodash';
import {useSnackbar} from 'notistack';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Grid} from '@mui/material';

import {MapDetails} from './components/MapDetails';
import {MapSelect} from './components/MapSelect';
import {SearchConditions, SearchType} from './types';

interface MainPanelProps {
  searchConditions: SearchConditions | null;
}

export const MainPanel = ({searchConditions}: MainPanelProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  const [mapList, setMapList] = useState<string[]>([]);
  const [selectedMaps, setSelectedMaps] = useState<string[]>([]);

  /***
   * X軸生成処理
   */
  const xTimeliles = useCallback(() => {
    if (searchConditions === null) return [];

    let datelist: any = [];
    let from_day = new Date(searchConditions.selectDateFrom);
    let to_day = new Date(searchConditions.selectDateTo);

    switch (searchConditions.searchType) {
      case SearchType.WORK:
        return [];
      case SearchType.HOUR:
        for (let d = from_day; d <= to_day; d.setHours(d.getHours() + 1)) {
          datelist.push(dayjs(d).format('YYYY-MM-DD HH'));
        }
        return datelist.sort();
      case SearchType.DAY:
        for (let d = from_day; d <= to_day; d.setDate(d.getDate() + 1)) {
          datelist.push(dayjs(d).format('YYYY-MM-DD'));
        }
        return datelist.sort();
      case SearchType.MONTH:
        let start_month_1day = new Date(from_day.setDate(1));
        let end_month_1day = new Date(to_day.setDate(1));
        for (
          start_month_1day;
          start_month_1day <= end_month_1day;
          start_month_1day.setMonth(start_month_1day.getMonth() + 1)
        ) {
          datelist.push(dayjs(start_month_1day).format('YYYY-MM'));
        }
        return datelist.sort();
    }
  }, [
    searchConditions?.selectDateFrom,
    searchConditions?.selectDateTo,
    searchConditions?.searchType
  ]);

  /**
   * 初期処理
   */
  useEffect(() => {
    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  /**
   * 検索処理
   */
  useEffect(() => {
    if (searchConditions === null) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setMapList([]);

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      // Cloud Distance Map取得
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 30004,
        parameters: {
          select_date_from: searchConditions.selectDateFrom,
          select_date_to: searchConditions.selectDateTo,
          work: !isEmpty(searchConditions.work)
            ? '{' + searchConditions.work.join(',') + '}'
            : null,
          deviceid: !isEmpty(searchConditions.deviceid)
            ? '{' + searchConditions.deviceid.join(',') + '}'
            : null,
          judge: searchConditions.judge === 9 ? null : searchConditions.judge
        },
        name: '部位データ',
        cancelToken: cancelSource.current.token,
        t
      }).then(data => {
        const maps = data.map((item: {map: string}[]) => item.map);
        setMapList(maps);
      })
    ]).finally(() => {
      setStartProcess(false);
    });
  }, [searchConditions]);

  useEffect(() => {
    //最初の１件目を選択状態にする
    setSelectedMaps(0 < mapList.length ? [mapList[0]] : []);
  }, [mapList]);

  return searchConditions && 0 < mapList.length ? (
    <ProgressAria startProcess={startProcess} loadingMessage>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={3} md={2}>
          <MapSelect
            mapSeries={mapList}
            selectedValues={selectedMaps}
            setSelectedValues={setSelectedMaps}
          />
        </Grid>
        <Grid item xs={12} sm={9} md={10}>
          <MapDetails
            mapList={selectedMaps}
            searchConditions={searchConditions}
            xTimeliles={xTimeliles()}
          />
        </Grid>
      </Grid>
    </ProgressAria>
  ) : null;
};
